import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { useLang } from "../contexts/LanguageContext";
import { TrendingDown, ArrowRight } from "lucide-react";

/**
 * Coached re-drill chip — surfaces the user's lowest-scoring subtopic
 * from /api/stats/weak-subtopics and offers a direct CTA to practice it.
 * Hidden when there isn't enough data (< 3 attempts on any subtopic).
 */
export default function WeakSubtopicsChip() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/stats/weak-subtopics?lang=${lang}`).then(r => setData(r.data)).catch(() => setData(null));
  }, [lang]);

  if (!data?.suggestion) return null;
  const s = data.suggestion;
  const tone =
    s.accuracy < 40 ? "bg-rose-50 border-rose-200 text-rose-900"
    : "bg-amber-50 border-amber-200 text-amber-900";

  return (
    <Card data-testid="weak-subtopic-chip" className={`p-5 mt-6 border ${tone}`}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-10 h-10 rounded-md bg-white/70 grid place-items-center shrink-0">
          <TrendingDown className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <Badge variant="outline" className="mb-1 text-[10px] uppercase tracking-wide bg-white">
            {t("weakSubtopicLabel")}
          </Badge>
          <h3 className="font-heading text-base font-semibold leading-snug">
            {t("weakSubtopicHeadline")}{" "}
            <span className="underline decoration-dotted" data-testid="weak-subtopic-title">
              {s.title}
            </span>
            {" "}
            <span className="text-sm font-normal opacity-80">({s.accuracy}%)</span>
          </h3>
          <p className="text-sm mt-1 opacity-80">{t("weakSubtopicHint")}</p>
        </div>
        <Link to={`/quiz?mode=practice&count=10&subtopic=${s.subtopic_id}`} className="shrink-0">
          <Button data-testid="weak-subtopic-cta" className="bg-fjord hover:bg-fjord-700">
            {t("weakSubtopicCta")} <ArrowRight className="w-4 h-4 ms-1.5 rtl:rotate-180" />
          </Button>
        </Link>
      </div>
    </Card>
  );
}
